import { Link } from 'react-router-dom';
import { Building2, ClipboardCheck, ShieldAlert, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import { PANEL } from '@/lib/surface';

/**
 * Band three: the part of the dashboard that depends on who is reading it.
 *
 * Bands one and two are the same for everyone — a blocked job is a blocked job.
 * What differs is what a reader can do about the rest of the platform: an
 * administrator cares about tenancy and accounts, a manager about the queue of
 * things waiting on their decision, and a viewer about neither.
 *
 * Keyed on permissions rather than on role names. Roles here are data — a
 * company can define its own — so `roles.includes('Admin')` would work for the
 * seeded set and quietly fail for anyone who renamed it. Permissions are what
 * the API actually enforces, so a card that appears is a card whose page will
 * open.
 */

interface Tile {
  label: string;
  value: number | string;
  hint: string;
  href: string;
  icon: typeof Building2;
  tone?: 'default' | 'warn';
}

function TileCard({ tile }: { tile: Tile }) {
  const Icon = tile.icon;
  return (
    <Link
      to={tile.href}
      className={cn(
        PANEL,
        'group flex items-center gap-4 p-4 transition-colors hover:border-primary/40 hover:bg-muted/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
      )}
    >
      <span
        className={cn(
          'flex h-10 w-10 flex-none items-center justify-center rounded-lg',
          tile.tone === 'warn'
            ? 'bg-status-warning-subtle text-status-warning-foreground'
            : 'bg-muted text-muted-foreground'
        )}
      >
        <Icon className="h-5 w-5" />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-xs font-medium uppercase tracking-wide text-muted-foreground">
          {tile.label}
        </span>
        <span className="block text-xl font-semibold tabular-nums text-foreground">
          {tile.value}
        </span>
        <span className="block truncate text-xs text-muted-foreground">{tile.hint}</span>
      </span>
      <ChevronRight className="h-4 w-4 flex-none text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100" />
    </Link>
  );
}

export function RoleBand({
  hasPermission,
  pendingApprovals,
  activeAlerts,
  companyCount,
}: {
  hasPermission: (permission: string) => boolean;
  pendingApprovals: number;
  activeAlerts: number;
  companyCount: number | null;
}) {
  const tiles: Tile[] = [];

  // Approvals: anyone who can act on them. A queue shown to someone who cannot
  // clear it is a nag, not a worklist.
  if (hasPermission('approval:read')) {
    tiles.push({
      label: 'Awaiting your decision',
      value: pendingApprovals,
      hint: pendingApprovals === 0 ? 'nothing queued' : 'requests in the approvals queue',
      href: '/approvals',
      icon: ClipboardCheck,
      tone: pendingApprovals > 0 ? 'warn' : 'default',
    });
  }

  if (hasPermission('alert:read')) {
    tiles.push({
      label: 'Open alerts',
      value: activeAlerts,
      hint: activeAlerts === 0 ? 'none raised' : 'raised and not yet cleared',
      href: '/alerts',
      icon: ShieldAlert,
      tone: activeAlerts > 0 ? 'warn' : 'default',
    });
  }

  // Tenancy. Only meaningful to whoever administers the platform, and
  // companyCount is null for anyone the API refused.
  if (hasPermission('company:read') && companyCount != null) {
    tiles.push({
      label: 'Companies',
      value: companyCount,
      hint: 'tenant organisations on the platform',
      href: '/admin/companies',
      icon: Building2,
    });
  }

  // A viewer sees neither, and gets no empty band rather than an empty panel.
  if (tiles.length === 0) return null;

  return (
    <div
      className={cn(
        'grid grid-cols-1 gap-5',
        tiles.length === 2 ? 'sm:grid-cols-2' : 'sm:grid-cols-2 lg:grid-cols-3'
      )}
    >
      {tiles.map((t) => (
        <TileCard key={t.label} tile={t} />
      ))}
    </div>
  );
}
